import { DialogM } from "@common/components/alert/dialog/Dialog"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import {
    DimensionsContext,
    IS_WEB,
    isSafari,
    isWebMobile,
} from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { putNextScreenParams } from "@data/redux/actions/navigation"
import { authService } from "@data/services/authService"
import { plr } from "@data/utility/Utils"
import { hasBothSubjectTypes } from "@data/utility/dataUtils"
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import Constants from "expo-constants"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { FlatList, RefreshControl, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import ChampionBubble from "../../student/champions/ChampionBubble"
import ApplicationFinishedScreen from "./ApplicationFinishedScreen"
import FileStep from "./FileStep"
import { SliderNavigationM } from "./SliderNavigationM"

const TutorApplicationScreen = (props) => {
    const dimensions = useContext(DimensionsContext)
    const flatListRef = useRef()
    const applicationSteps = props.user?.application_steps

    const itemHeight = 500
    const stepsCount = applicationSteps?.length
    const hasBothTypes = hasBothSubjectTypes(
        props.user?.profile?.relevant_subjects || []
    )

    const currentStep = props.user?.application_current_step?.step

    const completion = props.user?.application_current_step?.completion
    const navigation = useNavigation()
    const applicationFinished =
        props.user?.application_current_step?.step === stepsCount &&
        completion >= 100
    const initialIndex = currentStep > 0 ? currentStep - 1 : 0
    const [currentVisibleIndex, setCurrentVisibleIndex] = useState(initialIndex)

    const currentStepName = applicationSteps?.[currentStep - 1]?.test_category
    const currentStepChampion =
        currentStepName === "motivation"
            ? "tina"
            : currentStepName === "educational"
            ? "zoe"
            : currentStepName === "technical"
            ? "sam"
            : "abou"

    // Helper function to format step display
    // All steps display as stepNum - 1 (0-indexed from user perspective)
    // For candidates with both types: step 6 → "5", step 7 → "5.5"
    const getStepDisplay = (stepNum) => {
        const baseDisplay = stepNum

        if (!hasBothTypes) return baseDisplay

        // If has both types and this is the second technical step
        const step = applicationSteps?.[stepNum - 1]
        if (step?.test_category === "technical" && stepNum === 7) {
            return "6.5"
        }

        return baseDisplay
    }

    // Total display count (always 6 from user perspective)
    const displayStepsCount = 6
    const remainingStepsCount = displayStepsCount - currentStep + 1

    useFocusEffect(
        useCallback(() => {
            props.apiRequest(authService.getUser)
        }, [])
    )

    useEffect(() => {
        if (currentStep > 0 && stepsCount > 0) {
            setCurrentVisibleIndex(currentStep - 1)
        }
    }, [currentStep])

    useEffect(() => {
        flatListRef?.current?.scrollToIndex({ index: currentVisibleIndex })
    }, [currentVisibleIndex])

    useEffect(() => {
        if (currentStep === 1) {
            setTimeout(() => {
                navigation.push("intro", {
                    name: "abou",
                    introSuffix: `_application_interview_intro`,
                })
            }, 300)
        }
    }, [])

    const showDialog = IS_WEB && (isWebMobile() || isSafari())
    const downloadAppDialog = (champion, variant = "main") => {
        const titre = isWebMobile()
            ? "Télécharge l'application mobile"
            : "Télécharge l'application mobile ou connecte-toi depuis le navigateur Google Chrome"
        const description =
            "Cela est nécessaire pour répondre à la question suivante en vidéo."
        const btnText = "Télécharger"
        DialogM.show({
            text1: titre,
            text2: description,
            champion: champion,
            variant: variant,
            buttonText1: btnText,
            onPress: () => {
                if (navigator.userAgent.toLowerCase().includes("android")) {
                    window.location =
                        props.androidAppUrl ??
                        Constants.expoConfig.android.playStoreUrl
                } else {
                    window.location =
                        props.iosAppUrl ?? Constants.expoConfig.ios.appStoreUrl
                }
            },
            buttonText2: "Retour",
            onPressBack: () => {},
        })
    }

    return (
        <View
            style={{
                flex: 1,
                alignSelf: "center",
                width: dimensions.width,
                marginTop: 20,
            }}
        >
            <ScrollView
                refreshControl={
                    <RefreshControl
                        refreshing={!!props.getUserRequest}
                        onRefresh={() => props.apiRequest(authService.getUser)}
                        containerStyle={{
                            flex: 1,
                            width: dimensions?.width,
                        }}
                    />
                }
            >
                {applicationFinished ? (
                    <ApplicationFinishedScreen />
                ) : (
                    <>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={20}
                            color={theme.colors.primary}
                            style={{
                                marginLeft: 10,
                            }}
                        >
                            MA CANDIDATURE
                        </TextM>
                        <View
                            style={{
                                width: dimensions?.width,
                                flex: 1,
                            }}
                        >
                            {applicationSteps?.length > 0 && (
                                <FlatList
                                    data={applicationSteps}
                                    showsHorizontalScrollIndicator={false}
                                    ref={flatListRef}
                                    horizontal={true}
                                    pagingEnabled
                                    style={{
                                        width: dimensions?.width,
                                        flex: 1,
                                    }}
                                    initialScrollIndex={currentVisibleIndex}
                                    extraData={currentStep}
                                    key={`flatlist-${currentStep}`}
                                    getItemLayout={(_, index) => ({
                                        length: dimensions?.width,
                                        offset: dimensions?.width * index,
                                        index,
                                    })}
                                    renderItem={(item) => {
                                        const stepNum = item?.index + 1
                                        const step = item?.item
                                        const isVideoStep =
                                            step?.description?.includes(
                                                "video"
                                            ) ||
                                            step?.description?.includes(
                                                "vidéo"
                                            ) ||
                                            currentStepName === "technical"

                                        const onStepPress = () => {
                                            if (
                                                IS_WEB &&
                                                showDialog &&
                                                isVideoStep
                                            )
                                                return downloadAppDialog(
                                                    currentStepChampion
                                                )
                                            if (stepNum === 1)
                                                navigation.push(
                                                    "application-personal-infos"
                                                )
                                            else {
                                                props.putNextScreenParams({
                                                    testCategory: isVideoStep
                                                        ? currentStepName +
                                                          "_video"
                                                        : currentStepName + "_",
                                                    stepNum,
                                                })

                                                // Use intro_suffix from step if available, otherwise use default
                                                const introSuffix =
                                                    step?.intro_suffix ||
                                                    `_${
                                                        isVideoStep
                                                            ? "video"
                                                            : currentStepName
                                                    }_interview_intro`

                                                navigation.push("intro", {
                                                    name: currentStepChampion,
                                                    introSuffix: introSuffix,
                                                    isTracked: false,
                                                    goBack: true,
                                                    nextScreen:
                                                        "application-interview",
                                                })
                                            }
                                        }
                                        return (
                                            <View
                                                style={{
                                                    height: itemHeight,
                                                    alignItems: "flex-start",
                                                    justifyContent:
                                                        "flex-start",
                                                    width: dimensions?.width,
                                                    paddingTop: 20,
                                                    opacity:
                                                        currentStep >= stepNum
                                                            ? 1
                                                            : 0.5,
                                                }}
                                            >
                                                <View
                                                    style={{
                                                        alignItems:
                                                            "flex-start",
                                                        justifyContent:
                                                            "center",
                                                        paddingRight: 20,
                                                    }}
                                                >
                                                    <FileStep
                                                        stepNum={stepNum}
                                                        stepInfo={step}
                                                        currentStep={
                                                            currentStep
                                                        }
                                                        onPress={onStepPress}
                                                    />
                                                </View>
                                                <View
                                                    style={{
                                                        marginLeft: 40,
                                                    }}
                                                >
                                                    <View
                                                        style={{
                                                            height: 20,
                                                            width: dimensions?.width,
                                                            backgroundColor:
                                                                theme.colors
                                                                    .lighter,
                                                        }}
                                                    />
                                                    <View
                                                        style={{
                                                            height: 20,
                                                            width:
                                                                stepNum ===
                                                                currentStep
                                                                    ? 20 +
                                                                      (dimensions?.width *
                                                                          completion) /
                                                                          100
                                                                    : stepNum <
                                                                      currentStep
                                                                    ? dimensions?.width
                                                                    : 0,
                                                            backgroundColor:
                                                                theme.colors
                                                                    .primary,
                                                            position:
                                                                "absolute",
                                                            left:
                                                                stepNum === 1
                                                                    ? 10
                                                                    : 0,
                                                        }}
                                                    />
                                                    <View
                                                        style={{
                                                            height: 40,
                                                            width: 40,
                                                            borderRadius: 20,
                                                            backgroundColor:
                                                                currentStep >
                                                                stepNum
                                                                    ? theme
                                                                          .colors
                                                                          .success
                                                                    : theme
                                                                          .colors
                                                                          .white,
                                                            borderWidth: 5,
                                                            borderColor:
                                                                theme.colors
                                                                    .primary,
                                                            position:
                                                                "absolute",
                                                            left: 0,
                                                            top: -10,
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        {currentStep >
                                                            stepNum && (
                                                            <FontAwesome5
                                                                style={{
                                                                    backgroundColor:
                                                                        theme
                                                                            .colors
                                                                            .success,
                                                                }}
                                                                name={"check"}
                                                                size={20}
                                                                color={
                                                                    theme.colors
                                                                        .white
                                                                }
                                                            />
                                                        )}
                                                    </View>
                                                    <View
                                                        style={{
                                                            backgroundColor:
                                                                theme.colors
                                                                    .primary,
                                                            width: 3,
                                                            height: 20,
                                                            position:
                                                                "absolute",
                                                            left: 20 - 2,
                                                            top: -25,
                                                        }}
                                                    />
                                                </View>
                                                <View
                                                    style={{
                                                        alignItems:
                                                            "flex-start",
                                                        marginTop: 15,
                                                        marginLeft: 42,
                                                    }}
                                                >
                                                    <TextM
                                                        color={
                                                            theme.colors.primary
                                                        }
                                                        fontSize={16}
                                                        fontWeight={"Bold"}
                                                        style={{
                                                            marginBottom: 5,
                                                        }}
                                                    >
                                                        ÉTAPE{" "}
                                                        {getStepDisplay(
                                                            stepNum
                                                        )}
                                                    </TextM>
                                                    <TextM
                                                        wrap
                                                        style={{
                                                            textAlign: "left",
                                                            maxWidth:
                                                                dimensions?.width -
                                                                80,
                                                            marginBottom: 15,
                                                        }}
                                                    >
                                                        {step?.description}
                                                    </TextM>
                                                    {stepNum < currentStep ? (
                                                        <TextM
                                                            wrap
                                                            style={{
                                                                textAlign:
                                                                    "left",
                                                            }}
                                                            fontWeight={
                                                                "SemiBold"
                                                            }
                                                            color={
                                                                theme.colors
                                                                    .success
                                                            }
                                                            fontSize={16}
                                                        >
                                                            TERMINÉ !
                                                        </TextM>
                                                    ) : stepNum ===
                                                      currentStep ? (
                                                        <FilledButtonM
                                                            onPress={
                                                                onStepPress
                                                            }
                                                            style={{
                                                                width: 100,
                                                                alignSelf:
                                                                    "flex-start",
                                                            }}
                                                            label={
                                                                props.getUserRequest
                                                                    ? ""
                                                                    : "GO !"
                                                            }
                                                            disabled={
                                                                props.getUserRequest
                                                            }
                                                            loading={
                                                                props.getUserRequest
                                                            }
                                                        />
                                                    ) : null}
                                                </View>
                                            </View>
                                        )
                                    }}
                                />
                            )}
                            <SliderNavigationM
                                style={{
                                    width: dimensions?.width,
                                    position: "absolute",
                                    alignSelf: "center",
                                    top: 194,
                                }}
                                currentIndex={currentVisibleIndex}
                                setCurrentIndex={setCurrentVisibleIndex}
                                flatListRef={flatListRef}
                                data={applicationSteps}
                            />
                        </View>
                    </>
                )}
            </ScrollView>
            {!applicationFinished && (
                <ChampionBubble
                    name={currentStepChampion}
                    variant={"main"}
                    championPosition={"left"}
                    style={{
                        position: "absolute",
                        bottom: 0,
                    }}
                    texts={[
                        `Il reste ${Math.max(remainingStepsCount, 1)} ${plr(
                            "étape",
                            Math.max(remainingStepsCount, 1)
                        )} pour finir ta candidature !`,
                    ]}
                />
            )}
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        getUserRequest: state.api.getUser?.request,
        androidAppUrl: state.data.staticData?.android_app_url,
        iosAppUrl: state.data.staticData?.ios_app_url,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putNextScreenParams,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TutorApplicationScreen)
